import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const ElevatingCoreCurriculumWithCuripod = () => {
  const post = getBlogPost('elevating-core-curriculum-with-curipod');
  return (
    <BlogArticle post={post}>
      <p>
        {
          'Every district invests heavily in core curriculum programs, which define what students must learn and ensure consistency across classrooms. These resources provide a strong foundation, but how students experience them depends on what happens in the classroom.'
        }
      </p>
      <p>
        {'That’s where teaching meets its most important challenge: '}
        <strong>
          {'making essential content engaging, interactive, and meaningful'}
        </strong>
        {
          '. Core curricula can be dense with standards and concepts, but with the right tools, teachers can transform them into lessons that spark curiosity, invite discussion, and keep students connected to their learning.'
        }
      </p>
      <p>
        {'As John Hattie’s research underscores, strategies like classroom discussion, '}
        <Link
          href="/c/blog-posts/why-feedback-fuels-learning-and-how-curipod-makes-it-easy"
          passHref
        >
          <a>{'timely feedback'}</a>
        </Link>
        {
          ', and active retrieval are among the most powerful ways to boost achievement. The challenge is embedding those strategies consistently into daily lessons without adding hours of prep to a teacher’s plate. '
        }
      </p>
      <p>
        {
          'Strong curriculum is a starting point. True impact comes from how it’s experienced in the classroom.'
        }
      </p>
      <h3>{'Using Curipod with Core Curriculum'}</h3>
      <p>{'Curipod is designed to amplify the curriculum teachers already use.'}</p>
      <p>
        {
          'It transforms static resources into interactive, student-centered lessons that spark dialogue, '
        }
        <Link
          href="/c/blog-posts/cooperative-learning-with-curipod-boosts-student-success"
          passHref
        >
          <a>{'critical thinking'}</a>
        </Link>
        {', and collaboration. As one curriculum leader shared, '}
        <em>
          {
            '“The fact that our kids got individualized feedback immediately, every day, was huge.”'
          }
        </em>
      </p>
      <p>{'Teachers can:'}</p>
      <ul>
        <li>
          <strong>{'Generate lessons from curriculum objectives.'}</strong>
          {' Start with a scope-and-sequence standard and instantly '}
          <Link href="/c/blog-posts/curify-your-core-curriculum" passHref>
            <a>{'build a ready-to-teach lesson'}</a>
          </Link>
          {'.'}
        </li>
        <li>
          <strong>{'Reteach or target specific needs.'}</strong>
          {
            ' Curipod makes creating small-group lessons simple or revisiting concepts that need reinforcement.'
          }
        </li>
        <li>
          <strong>{'Upload existing presentations.'}</strong>
          {
            ' District slide decks become interactive with built-in checks for understanding, polls, and discussions.'
          }
        </li>
        <li>
          <strong>{'Build from text.'}</strong>
          {
            ' Paste in a textbook excerpt, vocabulary list, or novel passage, and Curipod generates prompts, questions, and writing tasks that spark analysis.'
          }
        </li>
        <li>
          <strong>{'Integrate active learning.'}</strong>
          {
            ' With polls, peer feedback, and discussions, engagement and accountability are built in—without extra prep.'
          }
          <br />
          <br />
        </li>
      </ul>
      <p>
        {
          'Crucially, Curipod ensures every student has a voice. No one can hide in the back of the room or slip through a worksheet in silence. As one teacher put it, '
        }
        <em>
          {
            '“No one complains. They all are engaged… whether it be a test prep [or] a full lesson.”'
          }
        </em>
        {
          ' That kind of consistency turns curriculum into something students want to interact with, lesson after lesson.'
        }
      </p>
      <p>
        {
          'Whether it’s a scripted curriculum, a high-quality textbook, or a beloved novel, Curipod ensures every resource becomes a tool for active learning. By embedding strategies like immediate feedback and whole-class participation, lessons don’t just align with the curriculum—they come alive for students.'
        }
      </p>
      <p>
        {
          'This matters. John Hattie’s research highlights that strategies like classroom discussion (effect size 0.82) and '
        }
        <Link
          href="/c/blog-posts/make-thinking-visible-10-formative-assessments-that-center-student-voice"
          passHref
        >
          <a>{'formative assessment'}</a>
        </Link>
        {
          ' (0.90) are among the most powerful drivers of student growth. Curipod makes those strategies routine.'
        }
      </p>
      <p>
        {
          'By transforming the materials schools already invest in, Curipod helps ensure those resources achieve their full potential, keeping students engaged and teachers supported with features like:'
        }
      </p>
      <ul>
        <li>
          <Link href="/c/blog-posts/curify-your-core-curriculum" passHref>
            <a>
              <strong>{'Curify My Slides'}</strong>
            </a>
          </Link>
          <strong>{':'}</strong>
          {
            ' Upload a PowerPoint or Google Slides deck, and Curipod automatically adds polls, checks for understanding, and interactive prompts—turning a passive presentation into a two-way conversation.'
          }
        </li>
        <li>
          <Link
            href="/a7cdcbe5-cddc-5210-a452-d5d106b4266f/home?selectedCardId=curify-passage&viewAll=true"
            passHref
          >
            <a>
              <strong>{'Lessons from Text:'}</strong>
            </a>
          </Link>
          {
            ' Paste in a textbook passage, vocabulary list, or historical excerpt, and Curipod generates questions and writing tasks that drive deeper analysis.'
          }
        </li>
        <li>
          <Link
            href="/c/blog-posts/beyond-the-ela-classroom-how-to-improve-writing-instruction-across-the-curriculum"
            passHref
          >
            <a>
              <strong>{'Cross-Curricular Flexibility'}</strong>
            </a>
          </Link>
          <strong>{':'}</strong>
          {
            ' From science labs to social studies debates, Curipod adapts to any subject, embedding interactive strategies without requiring teachers to start from scratch.'
          }
        </li>
      </ul>
      <p>
        {
          'With Curipod, students don’t just receive content—they respond to it, discuss it, and refine their thinking in real time.'
        }
      </p>
      <h3>{'Why This Matters for Schools and Districts'}</h3>
      <p>
        {
          'Districts invest heavily in curriculum programs. Ensuring this investment delivers results is about enhancing—not replacing—those materials. For districts, this means that their curriculum investment goes further with Curipod.'
        }
      </p>
      <p>{'At the classroom level, this means:'}</p>
      <ul>
        <li>{'Teachers stay aligned with pacing guides and standards.'}</li>
        <li>
          {'Students receive feedback in real time, keeping them motivated and on track.'}
        </li>
        <li>
          {
            'Lessons that once felt flat now spark dialogue, critical thinking, and collaboration.'
          }
        </li>
      </ul>
      <p>{'At the district level, this means:'}</p>
      <ul>
        <li>
          {
            'Visibility into student participation and understanding through live insights.'
          }
        </li>
        <li>
          {
            'Greater consistency across classrooms without sacrificing teacher creativity.'
          }
        </li>
      </ul>
      <p>
        {'As Nadine Lynch, a curriculum supervisor, explained, '}
        <em>{'“It’s taken a big burden off of my teachers.”'}</em>
        {
          ' That support allows teachers to focus less on logistics and more on the heart of instruction.'
        }
      </p>
      <p>
        {
          'In short, Curipod makes it easier for teachers to do the right thing, embedding high-impact practices into daily instruction utilizing core curriculum, without adding more to their workload.'
        }
      </p>
      <h3>{'Bringing Out the Best in Core Curriculum'}</h3>
      <p>
        {
          'Kids don’t learn unless they’re engaged, and engagement isn’t an “extra”. It’s the foundation that makes curriculum work.'
        }
      </p>
      <p>
        {
          'Curipod doesn’t replace the resources districts invest in. Instead, it elevates them, embedding practices like discussion, feedback, and real-time assessment into every lesson. The result? Lessons that feel exciting, students who take ownership of their learning, and teachers who can focus on teaching instead of managing static materials.'
        }
      </p>
      <p>
        {
          'By making the best thing to do also the easiest thing to do, Curipod ensures core curriculum is more than just content. It’s a catalyst for '
        }
        <Link href="/c/blogs" passHref>
          <a>{'deeper learning'}</a>
        </Link>
        {', stronger achievement, and classrooms where students truly thrive.'}
      </p>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default ElevatingCoreCurriculumWithCuripod;
